import axios from 'axios';
import {domain} from '../config/auth';
import { getToken, removeToken } from './tokenStorage';
const API_BASE_URL = domain || 'http://localhost:8080';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token if available
api.interceptors.request.use(
  (config) => {
    const token = getToken('token') || localStorage.getItem('tempToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor — any 401 forces login (except dashboard API calls)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    /*
     * 403 CONSENT_REQUIRED — the server is refusing because the member still
     * owes data-sharing consent (middleware/consentGate.middleware.js).
     *
     * Announced rather than handled here: this module has no React context, and
     * components/consent/ConsentGate.jsx listens for it and opens the dialog.
     * Deliberately NOT treated like a 401 — clearing the session and bouncing
     * to /login would lose the very session that needs to answer the question,
     * and the member would land back here on the next login.
     */
    if (error.response && error.response.status === 403
        && error.response.data && error.response.data.code === 'CONSENT_REQUIRED') {
      window.dispatchEvent(new CustomEvent('talexo:consent-required'));
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401) {
      const url = error.config?.url || '';
      // Don't redirect for dashboard API calls — let the component handle it
      if (!url.includes('/admin/dashboard/')) {
        // tokenStorage.getToken() checks BOTH localStorage and sessionStorage
        // (that's how "Remember me" unchecked survives a tab close), but this
        // used to clear only localStorage. A session persisted to sessionStorage
        // (Remember me OFF) would survive this "logout," so the very next
        // request — and the redirect target below — would still carry the
        // token the server just rejected, sending the user right back into a
        // 401 here. removeToken() clears both, so a real logout actually happens.
        removeToken('token');
        removeToken('tempToken');
        // Strip PUBLIC_URL (e.g. /ui) so the redirect target is a router path.
        const base = process.env.PUBLIC_URL || '';
        const pathname = base && window.location.pathname.startsWith(base)
          ? window.location.pathname.slice(base.length) || '/'
          : window.location.pathname;
        const currentPath = pathname + window.location.search;
        const isAlreadyOnLogin = currentPath.startsWith('/login');
        if (!isAlreadyOnLogin) {
          window.location.href = `${base}/login?session_expired=true&redirect=${encodeURIComponent(currentPath)}`;
        }
      }
    }
    return Promise.reject(error);
  }
);

// Helper functions for API calls
export const get = async (endpoint) => {
  try {
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const post = async (endpoint, data) => {
  try {
    const response = await api.post(endpoint, data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const put = async (endpoint, data) => {
  try {
    const response = await api.put(endpoint, data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const del = async (endpoint) => {
  try {
    const response = await api.delete(endpoint);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// Upload files with multipart/form-data
export const uploadFile = async (endpoint, file, additionalData = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add additional data to form data
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });
    
    const response = await api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// Centralized error handling
const handleApiError = (error) => {
  let errorMessage = 'An unexpected error occurred. Please try again later.';
  
  if (error.response) {
    // Server responded with non-2xx status
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        errorMessage = data.message || 'Invalid request. Please check your input.';
        break;
      case 401:
        errorMessage = data.message || 'Authentication failed. Please log in again.';
        break;
      case 403:
        errorMessage = data.message || 'You do not have permission to perform this action.';
        break;
      case 404:
        errorMessage = data.message || 'The requested resource was not found.';
        break;
      case 422:
        errorMessage = data.message || 'Validation error. Please check your input.';
        break;
      case 429:
        errorMessage = data.message || 'Too many requests. Please try again later.';
        break;
      case 500:
        errorMessage = data.message || 'Server error. Please try again later.';
        break;
      default:
        errorMessage = data.message || `Error (${status}): Please try again later.`;
    }
  } else if (error.request) {
    // Request was made but no response received (network error)
    errorMessage = 'Network error. Please check your connection and try again.';
  }
  
  console.error('API Error:', errorMessage, error);
  return errorMessage;
};

export default api;